/**
 * WHENEVER ATELIER — US Shared Components & Navigation Script
 * Controls Drawer, Search, Cart rendering, Toast, Keyboard A11y
 */

document.addEventListener("DOMContentLoaded", function () {
  initHeaderAndNav();
  initCartDrawer();
  initSearchOverlay();
  initGlobalEvents();
  initNewsletterForms();
  updateBagBadges();
});

let overlayReturnFocus = null;

function focusOverlay(target) {
  if (!overlayReturnFocus) overlayReturnFocus = document.activeElement;
  if (target) setTimeout(() => target.focus(), 150);
}

function restoreOverlayFocus() {
  const el = overlayReturnFocus;
  overlayReturnFocus = null;
  if (el && el !== document.body && document.contains(el)) el.focus();
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapTab(e, container) {
  const items = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => el.getClientRects().length > 0);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (!container.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function syncMenuExpanded() {
  const toggle = document.getElementById("mobileToggle");
  const drawer = document.getElementById("mobileDrawer");
  if (toggle && drawer) toggle.setAttribute("aria-expanded", String(drawer.classList.contains("active")));
}
function syncCartExpanded() {
  const drawer = document.getElementById("cartDrawer");
  const isOpen = !!(drawer && drawer.classList.contains("active"));
  document.querySelectorAll(".open-cart-trigger").forEach(btn => {
    btn.setAttribute("aria-expanded", String(isOpen));
  });
  if (!isOpen) {
    const statusEl = document.getElementById("cartDrawerStatus");
    if (statusEl) statusEl.textContent = "";
  }
}

function initHeaderAndNav() {
  const mobileToggle = document.getElementById("mobileToggle");
  const mobileDrawer = document.getElementById("mobileDrawer");
  const mobileDrawerBackdrop = document.getElementById("mobileDrawerBackdrop");
  const mobileDrawerClose = document.getElementById("mobileDrawerClose");

  function openMobileNav() {
    if (mobileDrawer) mobileDrawer.classList.add("active");
    if (mobileDrawerBackdrop) mobileDrawerBackdrop.classList.add("active");
    document.body.classList.add("scroll-locked");
    focusOverlay(mobileDrawerClose);
    syncMenuExpanded();
  }

  function closeMobileNav() {
    if (mobileDrawer) mobileDrawer.classList.remove("active");
    if (mobileDrawerBackdrop) mobileDrawerBackdrop.classList.remove("active");
    document.body.classList.remove("scroll-locked");
    restoreOverlayFocus();
    syncMenuExpanded();
  }

  if (mobileToggle) mobileToggle.addEventListener("click", openMobileNav);
  if (mobileDrawerClose) mobileDrawerClose.addEventListener("click", closeMobileNav);
  if (mobileDrawerBackdrop) mobileDrawerBackdrop.addEventListener("click", closeMobileNav);

  // Mobile accordion — any nav link with children
  document.querySelectorAll(".mobile-sub-trigger").forEach(function (trigger) {
    const submenu = trigger.parentElement ? trigger.parentElement.querySelector(".mobile-sub-menu") : null;
    if (!submenu) return;
    trigger.addEventListener("click", function () {
      const open = submenu.style.display === "flex";
      submenu.style.display = open ? "none" : "flex";
      trigger.classList.toggle("open", !open);
      trigger.setAttribute("aria-expanded", String(!open));
    });
  });

  document.querySelectorAll(".nav-item-dropdown").forEach(function (item) {
    item.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || item.classList.contains("is-dismissed")) return;
      if (!item.matches(":hover") && !item.matches(":focus-within")) return;
      item.classList.add("is-dismissed");
      const link = item.querySelector(".nav-link");
      if (link && item.contains(document.activeElement) && document.activeElement !== link) link.focus();
    });
    item.addEventListener("mouseleave", function () {
      item.classList.remove("is-dismissed");
    });
    item.addEventListener("focusout", function (e) {
      if (!item.contains(e.relatedTarget)) item.classList.remove("is-dismissed");
    });
  });
}

let cartPageDirty = false;
let cartPageRefreshSeq = 0;

async function refreshCartPage() {
  const host = document.querySelector(".section-main-cart");
  if (!host || host.id.indexOf("shopify-section-") !== 0) return;
  const seq = ++cartPageRefreshSeq;
  try {
    const res = await fetch(`${window.location.pathname}?section_id=${host.id.replace("shopify-section-", "")}`, { credentials: "same-origin" });
    if (!res.ok || seq !== cartPageRefreshSeq) return;
    const doc = new DOMParser().parseFromString(await res.text(), "text/html");
    const fresh = doc.getElementById(host.id) || doc.body;
    host.innerHTML = fresh.innerHTML;
  } catch (err) {
    // Stale page markup is replaced on the next navigation; nothing to recover here.
  }
}

function initCartDrawer() {
  const cartTriggerBtns = document.querySelectorAll(".open-cart-trigger");
  const cartDrawer = document.getElementById("cartDrawer");
  const cartDrawerBackdrop = document.getElementById("cartDrawerBackdrop");
  const cartDrawerClose = document.getElementById("cartDrawerClose");
  const cartDrawerItems = document.getElementById("cartDrawerItems");

  function openCart() {
    if (cartDrawer) cartDrawer.classList.add("active");
    if (cartDrawerBackdrop) cartDrawerBackdrop.classList.add("active");
    syncCartExpanded();
    renderCartDrawerItems();
    document.body.classList.add("scroll-locked");
    focusOverlay(cartDrawerClose);
  }

  function closeCart() {
    if (cartDrawer) cartDrawer.classList.remove("active");
    if (cartDrawerBackdrop) cartDrawerBackdrop.classList.remove("active");
    syncCartExpanded();
    document.body.classList.remove("scroll-locked");
    restoreOverlayFocus();
  }

  cartTriggerBtns.forEach(btn => {
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      openCart();
    });
  });

  if (cartDrawerClose) cartDrawerClose.addEventListener("click", closeCart);
  if (cartDrawerBackdrop) cartDrawerBackdrop.addEventListener("click", closeCart);

  if (cartDrawerItems) {
    cartDrawerItems.addEventListener("click", function (e) {
      const btn = e.target.closest("[data-action]");
      const line = btn ? btn.closest(".cart-item") : null;
      if (!btn || !line) return;
      const lineId = line.dataset.lineId;
      const item = WHENEVER_STORE.getItems().find(i => i.id === lineId);
      if (!item) return;
      cartPageDirty = true;
      const action = btn.dataset.action;
      if (action === "remove") WHENEVER_STORE.removeItem(lineId);
      else if (action === "inc") WHENEVER_STORE.adjustQuantity(lineId, 1);
      else if (action === "dec") WHENEVER_STORE.adjustQuantity(lineId, -1);
      else WHENEVER_STORE.updateQuantity(lineId, item.quantity + (action === "inc" ? 1 : -1));
    });
  }

  // Re-render when cart updates
  window.addEventListener("whenever:cart-updated", function (e) {
    renderCartDrawerItems();
    updateBagBadges(e.detail.count);
    if (cartPageDirty && (!e.detail || e.detail.pending === 0)) {
      cartPageDirty = false;
      refreshCartPage();
    }
  });

  window.openCartDrawer = openCart;
  window.closeCartDrawer = closeCart;
}

function restoreDrawerFocus(restore, emptyStateEl) {
  if (!restore) return;
  const lines = Array.from(document.querySelectorAll("#cartDrawerItems .cart-item"));
  const sameLine = lines.find(l => l.dataset.lineId === restore.lineId);
  let target = sameLine ? sameLine.querySelector(`[data-action="${restore.action}"]`) : null;
  if (!target && lines.length) target = lines[Math.min(restore.index, lines.length - 1)].querySelector('[data-action="remove"]');
  if (!target && emptyStateEl) target = emptyStateEl.querySelector("a");
  if (target) target.focus();
}

function renderCartDrawerItems() {
  const esc = WHENEVER_STORE.escapeHtml;
  const i18n = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.i18n) || {};
  const label = (tpl, title) => esc((tpl || "").replace("%product%", title));
  const listContainer = document.getElementById("cartDrawerItems");
  const subtotalEl = document.getElementById("cartDrawerSubtotal");
  const shippingMsgEl = document.getElementById("cartDrawerShippingMsg");
  const shippingBarFill = document.getElementById("cartDrawerShippingBar");
  const emptyStateEl = document.getElementById("cartDrawerEmpty");
  const footerContentEl = document.getElementById("cartDrawerFooterContent");
  const statusEl = document.getElementById("cartDrawerStatus");
  const cartDrawer = document.getElementById("cartDrawer");

  if (!listContainer) return;

  const items = WHENEVER_STORE.getItems();
  const subtotal = WHENEVER_STORE.getSubtotal(items);
  const count = WHENEVER_STORE.getItemCount(items);

  let restore = null;
  const focused = document.activeElement;
  if (focused && listContainer.contains(focused)) {
    const focusedLine = focused.closest(".cart-item");
    restore = {
      lineId: focusedLine ? focusedLine.dataset.lineId : null,
      action: focused.dataset.action || "remove",
      index: focusedLine ? Array.from(listContainer.querySelectorAll(".cart-item")).indexOf(focusedLine) : 0,
    };
  }

  if (statusEl) {
    const drawerOpen = cartDrawer && cartDrawer.classList.contains("active");
    statusEl.textContent = drawerOpen
      ? (i18n.statusSummary || "").replace("%items%", String(count)).replace("%subtotal%", WHENEVER_STORE.formatMoney(subtotal))
      : "";
  }

  const threshold = WHENEVER_STORE.FREE_SHIPPING_THRESHOLD;
  if (items.length === 0) {
    listContainer.innerHTML = "";
    if (emptyStateEl) emptyStateEl.style.display = "block";
    if (footerContentEl) footerContentEl.style.display = "none";
    if (subtotalEl) subtotalEl.textContent = WHENEVER_STORE.formatMoney(0);
    if (threshold > 0) {
      if (shippingMsgEl) {
        shippingMsgEl.innerHTML = (i18n.freeShippingProgress || "").replace("{{ remaining }}", WHENEVER_STORE.formatMoneyShort(threshold));
      }
      if (shippingBarFill) {
        shippingBarFill.style.width = "0%";
      }
    }
    restoreDrawerFocus(restore, emptyStateEl);
    return;
  }

  if (emptyStateEl) emptyStateEl.style.display = "none";
  if (footerContentEl) footerContentEl.style.display = "block";

  // Render items
  listContainer.innerHTML = items.map(item => `
    <div class="cart-item" data-line-id="${esc(item.id)}">
      <div class="cart-item-image">
        <img src="${esc(item.image)}" alt="${esc(item.title)}" width="84" height="112" loading="lazy" />
      </div>
      <div class="cart-item-details">
        <div>
          <div class="cart-item-title">${esc(item.title)}</div>
          ${item.variantTitle ? `<div class="cart-item-meta">${esc(item.variantTitle)}</div>` : ""}
          <div class="cart-item-price">${item.originalPrice > item.price ? `<s>${WHENEVER_STORE.formatMoney(item.originalPrice)}</s> ` : ""}${WHENEVER_STORE.formatMoney(item.price)}</div>
        </div>
        <div class="cart-item-controls">
          <div class="qty-stepper">
            <button type="button" class="qty-btn" data-action="dec" aria-label="${label(i18n.qtyDecrease, item.title)}">-</button>
            <span class="qty-val">${item.quantity}</span>
            <button type="button" class="qty-btn" data-action="inc" aria-label="${label(i18n.qtyIncrease, item.title)}">+</button>
          </div>
          <button type="button" class="cart-item-remove" data-action="remove" aria-label="${label(i18n.removeItem, item.title)}">${esc(i18n.removeLabel || "")}</button>
        </div>
      </div>
    </div>
  `).join("");

  if (subtotalEl) {
    subtotalEl.textContent = WHENEVER_STORE.formatMoney(subtotal);
  }

  // Free shipping bar — threshold from theme settings via WHENEVER_DATA.store
  if (threshold > 0) {
    const remaining = Math.max(0, threshold - subtotal);
    if (shippingMsgEl) {
      if (remaining === 0) {
        shippingMsgEl.innerHTML = `<strong>${i18n.freeShippingUnlocked || ""}</strong>`;
      } else {
        shippingMsgEl.innerHTML = (i18n.freeShippingProgress || "").replace("{{ remaining }}", WHENEVER_STORE.formatMoneyShort(remaining));
      }
    }
    if (shippingBarFill) {
      const pct = Math.min(100, Math.round((subtotal / threshold) * 100));
      shippingBarFill.style.width = `${pct}%`;
    }
  }

  restoreDrawerFocus(restore, emptyStateEl);
}

function initSearchOverlay() {
  const esc = WHENEVER_STORE.escapeHtml;
  const searchTriggerBtns = document.querySelectorAll(".open-search-trigger");
  const searchOverlay = document.getElementById("searchOverlay");
  const searchClose = document.getElementById("searchClose");
  const searchInput = document.getElementById("searchInput");
  const searchQuickResults = document.getElementById("searchQuickResults");
  let searchTimer = null;
  let searchAbort = null;

  function cancelSearch() {
    clearTimeout(searchTimer);
    if (searchAbort) {
      searchAbort.abort();
      searchAbort = null;
    }
  }

  function openSearch() {
    if (searchOverlay) searchOverlay.classList.add("active");
    document.body.classList.add("scroll-locked");
    focusOverlay(searchInput);
  }

  function closeSearch() {
    cancelSearch();
    if (searchOverlay) searchOverlay.classList.remove("active");
    document.body.classList.remove("scroll-locked");
    if (searchInput) searchInput.value = "";
    if (searchQuickResults) searchQuickResults.innerHTML = "";
    restoreOverlayFocus();
  }

  function renderNoResults(q) {
    const noResTpl = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.i18n && WHENEVER_DATA.i18n.noResults) || "";
    searchQuickResults.innerHTML = noResTpl ? `<p style="grid-column: 1/-1; font-size: 0.85rem; color: var(--text-muted);">${noResTpl.replace("{{ terms }}", esc(q))}</p>` : "";
  }

  async function runSearch(q) {
    searchAbort = new AbortController();
    const signal = searchAbort.signal;
    const base = (window.themeConfig && themeConfig.routes.predictive_search_url) || "/search/suggest";
    try {
      const res = await fetch(`${base}.json?q=${encodeURIComponent(q)}&resources%5Btype%5D=product&resources%5Blimit%5D=3`, { signal, headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("search");
      const data = await res.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      if (products.length === 0) {
        renderNoResults(q);
        return;
      }
      searchQuickResults.innerHTML = products.map(p => `
        <a href="${esc(p.url)}" class="product-card" style="margin-bottom: 0;">
          <div class="product-card-media" style="aspect-ratio: 1; margin-bottom: 8px;">
            <img src="${esc(WHENEVER_STORE.sizedImage((p.featured_image && p.featured_image.url) || p.image, 400))}" alt="" width="200" height="200" loading="lazy" />
          </div>
          <div class="product-card-title" style="font-size: 0.8rem;">${esc(p.title)}</div>
          <div class="product-card-price" style="font-size: 0.8rem;">${WHENEVER_STORE.formatMoney(p.price)}</div>
        </a>
      `).join("");
    } catch (err) {
      if (err.name === "AbortError") return;
      renderNoResults(q);
    }
  }

  searchTriggerBtns.forEach(btn => {
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      openSearch();
    });
  });

  if (searchClose) searchClose.addEventListener("click", closeSearch);

  if (searchInput) {
    searchInput.addEventListener("input", function (e) {
      const q = e.target.value.trim();
      cancelSearch();
      if (!searchQuickResults) return;
      if (q.length < 2) {
        searchQuickResults.innerHTML = "";
        return;
      }
      searchTimer = setTimeout(() => runSearch(q), 200);
    });

    searchInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        const val = searchInput.value.trim();
        if (val) {
          window.location.href = `${(window.themeConfig && themeConfig.routes.search_url) || "/search"}?q=${encodeURIComponent(val)}`;
        }
      }
    });
  }
}

function updateBagBadges(count = null) {
  const currentCount = count !== null ? count : WHENEVER_STORE.getItemCount();
  const badges = document.querySelectorAll(".bag-count-badge");
  badges.forEach(b => {
    b.textContent = currentCount;
    b.style.display = currentCount > 0 ? "inline-flex" : "none";
  });
}

function initGlobalEvents() {
  // ESC key closes all active drawers, overlays and modals (A11y QA 18)
  document.addEventListener("keydown", function (e) {
    if (e.key === "Tab") {
      const layer = document.querySelector(".drawer.active, .search-overlay.active, .fancybox-modal.active");
      if (layer) trapTab(e, layer);
      return;
    }
    if (e.key === "Escape") {
      const activeDrawer = document.querySelector(".drawer.active");
      const activeModal = document.querySelector(".modal.active");
      const activeBackdrop = document.querySelector(".drawer-backdrop.active, .modal-backdrop.active");
      const activeSearch = document.querySelector(".search-overlay.active");

      if (activeDrawer) activeDrawer.classList.remove("active");
      if (activeModal) activeModal.classList.remove("active");
      if (activeBackdrop) activeBackdrop.classList.remove("active");
      if (activeSearch) activeSearch.classList.remove("active");
      document.body.classList.remove("scroll-locked");
      syncMenuExpanded();
      syncCartExpanded();
      restoreOverlayFocus();
    }
  });

  // Accordion Toggles
  document.querySelectorAll(".accordion-trigger").forEach(trigger => {
    trigger.addEventListener("click", function () {
      const item = this.closest(".accordion-item");
      const isExpanded = item.classList.contains("active");

      // Optional: close siblings if needed
      // item.parentElement.querySelectorAll('.accordion-item').forEach(el => el.classList.remove('active'));

      item.classList.toggle("active", !isExpanded);
      this.setAttribute("aria-expanded", !isExpanded);
    });
  });
}

function initNewsletterForms() {
  // Shopify customer forms post natively to /contact; enhance UX with an
  // AJAX submit so the page doesn't reload and the result lands in the
  // status region the email input already references via aria-describedby.
  document.querySelectorAll('form.newsletter-form').forEach(form => {
    if (form.dataset.waNewsletterBound) return;
    form.dataset.waNewsletterBound = "1";
    const emailInput = form.querySelector('input[type="email"], .newsletter-input');
    const statusId = emailInput ? emailInput.getAttribute("aria-describedby") : null;
    const statusEl = statusId ? document.getElementById(statusId) : null;
    const report = (message, isError) => {
      if (!statusEl) {
        if (message) showToast(message);
        return;
      }
      statusEl.textContent = message || "";
      statusEl.classList.toggle("newsletter-form__status--error", !!isError);
      if (emailInput) {
        if (isError) emailInput.setAttribute("aria-invalid", "true");
        else emailInput.removeAttribute("aria-invalid");
      }
    };
    if (emailInput) {
      emailInput.addEventListener("input", function () {
        if (emailInput.getAttribute("aria-invalid") === "true" || (statusEl && statusEl.classList.contains("newsletter-form__status--error"))) {
          report("", false);
        }
      });
    }
    form.addEventListener("submit", async function (e) {
      const email = emailInput ? emailInput.value.trim() : "";
      const i18n = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.i18n) || {};
      e.preventDefault();
      if (!email || !email.includes("@")) {
        report(i18n.newsletterInvalid, true);
        return;
      }
      if (this.dataset.waPending) return;
      this.dataset.waPending = "1";
      const submitBtn = this.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute("aria-busy", "true");
      report("", false);
      try {
        const res = await fetch(this.action, {
          method: "POST",
          body: new FormData(this),
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        });
        const landed = new URL(res.url || this.action, window.location.href);
        if (landed.pathname.indexOf("/challenge") !== -1) {
          window.location.assign(landed.href);
          return;
        }
        const posted = /(^|[?&])customer_posted=true(&|$)/.test(landed.search) || landed.pathname !== new URL(this.action, window.location.href).pathname;
        if (res.ok && posted) {
          report(i18n.newsletterSuccess, false);
          if (emailInput) emailInput.value = "";
        } else {
          report(i18n.newsletterInvalid, true);
        }
      } catch (err) {
        // Network failure — fall back to native submit so the form still reaches the server.
        HTMLFormElement.prototype.submit.call(this);
      } finally {
        delete this.dataset.waPending;
        if (submitBtn) submitBtn.removeAttribute("aria-busy");
      }
    });
  });
}

function showToast(message, a, b) {
  const duration = typeof a === "number" ? a : (typeof b === "number" ? b : 3500);
  let toast = document.getElementById("globalToast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "globalToast";
    toast.className = "toast-notice";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.setAttribute("aria-atomic", "true");
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add("show");

  clearTimeout(toast._timeout);
  clearTimeout(toast._clearTimeout);
  toast._timeout = setTimeout(() => {
    toast.classList.remove("show");
    toast._clearTimeout = setTimeout(() => {
      toast.textContent = "";
    }, 400);
  }, duration);
}

window.showToast = showToast;
